import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { PageToolbarComponent } from '../../components/page-toolbar/page-toolbar.component';
import { VendorService } from '../../services/vendor.service';
import { VendorDto } from '../../dto/vendor.dto';
import { QueryResult, QueryResultItem } from '../../components/common-dto/query.dto';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-vendor-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    InputTextModule,
    TableModule,
    ProgressSpinnerModule,
    PageToolbarComponent,
  ],
  template: `
    <pb-page-toolbar
      [header]="'Vendors'"
      [supportsAdd]="false"
      [supportsEdit]="false"
      [isEditing]="false"
    ></pb-page-toolbar>

    <div class="vendors-container">
      <div class="search-container">
        <span class="p-input-icon-right full-width">
          <i class="pi pi-search"></i>
          <input
            type="text"
            pInputText
            [(ngModel)]="searchQuery"
            (ngModelChange)="onSearchInput()"
            placeholder="Search vendors by name, contact, description, or booth number..."
            class="auto-width"
          />
        </span>
      </div>

      <p-table
        [value]="vendors"
        [paginator]="true"
        [rows]="10"
        [rowsPerPageOptions]="[10, 25, 50]"
        [totalRecords]="totalRecords"
        [lazy]="true"
        (onLazyLoad)="loadData($event)"
        [loading]="loading"
      >
        <ng-template pTemplate="header">
          <tr>
            <th>Name</th>
            <th>Contact Name</th>
            <th>Contact Email</th>
            <th>Contact Phone</th>
            <th>Booth Number</th>
            <th>Description</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-vendor>
          <tr>
            <td>{{ vendor.name }}</td>
            <td>{{ vendor.contactName || '-' }}</td>
            <td>{{ vendor.contactEmail }}</td>
            <td>{{ vendor.contactPhone || '-' }}</td>
            <td>{{ vendor.boothNumber || '-' }}</td>
            <td>{{ vendor.description || '-' }}</td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="6" class="text-center">
              No vendors found
            </td>
          </tr>
        </ng-template>
      </p-table>
    </div>
  `,
  styles: [
    `
      .vendors-container {
        padding: 1rem;
      }

      .search-container {
        margin-bottom: 1.5rem;
      }

      .full-width {
        width: 100%;
        display: block;
      }

      .auto-width {
        width: 100%;
      }

      .loading-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 3rem;
        gap: 1rem;
      }

      :host ::ng-deep .p-input-icon-right {
        width: 100%;
      }

      :host ::ng-deep .p-input-icon-right input {
        width: 100%;
      }
    `,
  ],
})
export class VendorListComponent implements OnInit {
  vendors: VendorDto[] = [];
  searchQuery: string = '';
  loading = true;
  totalRecords = 0;
  conferenceId: string = '';

  private searchSubject = new Subject<string>();

  constructor(
    private vendorService: VendorService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.conferenceId = this.route.snapshot.params['conferenceId'] || '';

    // Setup debounced search
    this.searchSubject
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe(() => {
        this.loadData({ first: 0, rows: 10 });
      });

    this.loadData({ first: 0, rows: 10 });
  }

  onSearchInput(): void {
    this.searchSubject.next(this.searchQuery);
  }

  loadData(event: any): void {
    this.loading = true;
    const skip = event.first || 0;
    const take = event.rows || 10;

    const queryParams: any = {
      skip,
      take,
    };

    if (this.searchQuery && this.searchQuery.trim() !== '') {
      queryParams.filter = this.searchQuery.trim();
    }

    this.vendorService.getVendors(this.conferenceId, queryParams).subscribe({
      next: (result: QueryResult<VendorDto>) => {
        this.vendors = result.items.map((item) => item.item);
        this.totalRecords = result.total;
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading vendors:', error);
        this.loading = false;
      },
    });
  }
}

